import moment from 'moment-timezone';

import type { Timestamp } from '@/types/common';

interface StorageExpire {
	time: number;
	unit: moment.unitOfTime.Diff;
}

// biome-ignore lint/suspicious/noExplicitAny: Init Biome
interface StorageDatas<T = any> {
	data: T;
	timestamp?: Timestamp;
}

const getStorageDiffTime = (
	storageTimestamp: Timestamp,
	expireUnit: moment.unitOfTime.Diff
) => {
	const storageTimestampFormatted = moment(storageTimestamp);
	return moment().diff(storageTimestampFormatted, expireUnit);
};

// biome-ignore lint/suspicious/noExplicitAny: Init Biome
const getStorageDatas = <T = any>(
	storageItem: string,
	expire?: StorageExpire
): StorageDatas<T> | null => {
	try {
		const storageDatas: StorageDatas<T> = JSON.parse(storageItem);
		if (
			storageDatas.timestamp &&
			expire &&
			getStorageDiffTime(storageDatas.timestamp, expire.unit) >= expire.time
		) {
			return null;
		}

		return storageDatas;
	} catch (error) {
		console.error('Failed to parse storage data:', error);
		return null;
	}
};

// biome-ignore lint/suspicious/noExplicitAny: Init Biome
export const getSessionStorage = <T = any>(
	storageKey: string,
	expire?: StorageExpire
): StorageDatas<T> | null => {
	const storageItem = sessionStorage.getItem(storageKey);
	if (!storageItem) {
		return null;
	}

	return getStorageDatas<T>(storageItem, expire);
};

// biome-ignore lint/suspicious/noExplicitAny: Init Biome
export const getLocalStorage = <T = any>(
	storageKey: string,
	expire?: StorageExpire
): StorageDatas<T> | null => {
	const storageItem = localStorage.getItem(storageKey);
	if (!storageItem) {
		return null;
	}

	return getStorageDatas<T>(storageItem, expire);
};

// biome-ignore lint/suspicious/noExplicitAny: Init Biome
export const setSessionStorage = <T = any>(
	storageKey: string,
	data: T,
	timestamp: Timestamp = moment().valueOf()
): boolean => {
	try {
		sessionStorage.setItem(storageKey, JSON.stringify({ timestamp, data }));
		return true;
	} catch (error) {
		console.error('Failed to write to sessionStorage:', error);
		return false;
	}
};

// biome-ignore lint/suspicious/noExplicitAny: Init Biome
export const setLocalStorage = <T = any>(
	storageKey: string,
	data: T,
	timestamp: Timestamp = moment().valueOf()
): boolean => {
	try {
		localStorage.setItem(storageKey, JSON.stringify({ timestamp, data }));
		return true;
	} catch (error) {
		console.error('Failed to write to localStorage:', error);
		return false;
	}
};

/**
 * Reads a raw localStorage boolean flag stored as the string "true".
 * Unlike getLocalStorage, this does not expect a JSON payload.
 */
export const getStorageItemByKey = (key: string): boolean => {
	return localStorage.getItem(key) === 'true';
};
